import React from 'react';
import { useLanguage, Language } from '../contexts/LanguageContext';
import { LegalPage, LegalDoc } from '../components/LegalContent';

// EN/ES finalized (drafted 2026-09-21, approved by Manuel; compliance section
// updated 2026-09-22 with verified text from Fla. Stat. § 501.1391, ch.
// 2025-82, s.3, effective 2025-07-01). HT/VI = best effort machine
// translation, pending native review — same convention used for the rest of
// the site's HT/VI content.
const DOCS: Record<Language, LegalDoc> = {
  es: {
    title: 'Términos de Servicio',
    updated: 'Última actualización: 22 de septiembre de 2026',
    intro: 'Al usar el sitio o contratar el servicio de Noor Passport Services (DBA of Noor Metabolico LLC), usted acepta estos términos.',
    sections: [{
      heading: 'Qué es Noor y qué NO es',
      blocks: [{
        type: 'p',
        text: 'Noor es un servicio privado de **preparación de formularios** de pasaporte. Le ayudamos a completar correctamente su propio formulario oficial, en su idioma. **Noor no es una agencia de gobierno, no es un despacho de abogados y no es notario.** No damos asesoría legal ni de inmigración. El trámite final ante el Departamento de Estado de EE. UU. lo presenta usted mismo, no Noor.'
      }]
    }, {
      heading: 'Cumplimiento del Estatuto de Florida § 501.1391',
      blocks: [{
        type: 'p',
        text: 'En cumplimiento del Estatuto de Florida § 501.1391 (vigente desde el 1 de julio de 2025), que exige que toda persona o negocio que ofrezca servicios relacionados con inmigración y que no sea abogado con licencia publique un aviso visible en su sitio web y en su lugar de negocio: **"NO SOY ABOGADO CON LICENCIA PARA EJERCER LA ABOGACÍA Y NO PUEDO DAR ASESORÍA LEGAL NI COBRAR HONORARIOS POR ASESORÍA LEGAL. NO ESTOY ACREDITADO PARA REPRESENTARLO EN ASUNTOS DE INMIGRACIÓN."**'
      }, {
        type: 'p',
        text: 'Nuestro proceso de servicio — firma del Contrato de Servicio, pago, cuestionario, revisión y entrega — es nuestra propia política interna de buenas prácticas, pensada para garantizar transparencia y dejar constancia de que todo el trabajo se realiza bajo su instrucción específica. **No es una cita textual del § 501.1391.** No se cobra antes de que usted firme el contrato de servicio.'
      }]
    }, {
      heading: 'Precios',
      blocks: [{
        type: 'p',
        text: 'Los precios se cobran por persona/caso, no por formulario individual, según la tarjeta de precio vigente en el sitio en el momento de la contratación. El add-on de foto de pasaporte y la traducción certificada son servicios aparte, con su propio precio. **La tarifa que cobra el gobierno de EE. UU. por el pasaporte se paga aparte, directamente al gobierno — Noor no la cobra ni la incluye en su precio.**'
      }]
    }, {
      heading: 'Su responsabilidad como cliente',
      blocks: [{
        type: 'p',
        text: 'Usted es responsable de que los datos que nos entrega (nombre, fecha de nacimiento, lugar de nacimiento y demás) sean exactos y coincidan con sus documentos oficiales de identidad. Antes de la entrega final, usted revisa y aprueba el formulario preparado.'
      }]
    }, {
      heading: 'Garantía de corrección gratis',
      blocks: [{
        type: 'p',
        text: 'Si el formulario que preparamos tiene un error nuestro, lo corregimos y lo volvemos a entregar sin costo adicional, dentro de un plazo razonable después de la entrega. **Esta garantía cubre únicamente el formulario que preparamos.** No garantiza que el gobierno apruebe, acelere o no rechace su trámite, y no cubre la tarifa que usted pagó al gobierno.'
      }]
    }, {
      heading: 'Foto de pasaporte (si la contrata)',
      blocks: [{
        type: 'p',
        text: 'La foto se entrega **validada** contra las reglas oficiales vigentes — nunca la ofrecemos como "garantizada". Si la validación falla, usted tiene derecho a una re-toma sin costo; vea la Política de Reembolsos para los detalles.'
      }]
    }, {
      heading: 'Comunicación y asistente virtual',
      blocks: [{
        type: 'p',
        text: 'Podemos comunicarnos con usted por WhatsApp, teléfono, correo o el asistente virtual del sitio, en el idioma que usted eligió. El asistente virtual es automático; para casos que lo requieran, Manuel (el especialista humano del negocio) hace la revisión final del formulario.'
      }]
    }, {
      heading: 'Límites de responsabilidad',
      blocks: [{
        type: 'p',
        text: 'Noor no controla los tiempos ni las decisiones del Departamento de Estado ni de ninguna agencia de gobierno. No somos responsables por retrasos, rechazos o cambios de requisitos que estén fuera de nuestro control, siempre que el formulario que preparamos refleje correctamente la información que usted nos entregó y aprobó.'
      }]
    }, {
      heading: 'Cambios a estos términos',
      blocks: [{
        type: 'p',
        text: 'Podemos actualizar estos términos ocasionalmente; la fecha de última actualización arriba indica la versión vigente.'
      }]
    }, {
      heading: 'Ley aplicable',
      blocks: [{
        type: 'p',
        text: 'Estos términos se rigen por las leyes del Estado de Florida, EE. UU.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  },
  en: {
    title: 'Terms of Service',
    updated: 'Last updated: September 22, 2026',
    intro: 'By using the site or hiring the service of Noor Passport Services (DBA of Noor Metabolico LLC), you accept these terms.',
    sections: [{
      heading: 'What Noor is and is NOT',
      blocks: [{
        type: 'p',
        text: 'Noor is a private passport **form-preparation** service. We help you correctly complete your own official form, in your language. **Noor is not a government agency, a law firm, or a notary.** We do not provide legal or immigration advice. You submit the final application to the U.S. Department of State yourself — Noor does not.'
      }]
    }, {
      heading: 'Compliance with Florida Statute § 501.1391',
      blocks: [{
        type: 'p',
        text: 'In compliance with Florida Statute § 501.1391 (effective July 1, 2025), which requires any person or business offering immigration-related services who is not a licensed attorney to post a conspicuous notice on its website and place of business: **"I AM NOT AN ATTORNEY LICENSED TO PRACTICE LAW AND MAY NOT GIVE LEGAL ADVICE OR ACCEPT FEES FOR LEGAL ADVICE. I AM NOT ACCREDITED TO REPRESENT YOU IN IMMIGRATION MATTERS."**'
      }, {
        type: 'p',
        text: 'Our service process — signing the Service Agreement, payment, questionnaire, review, and delivery — is our own internal best-practice policy, designed to ensure transparency and document that all work is done at your specific direction. **It is not a verbatim quote from § 501.1391.** You are not charged before signing the service contract.'
      }]
    }, {
      heading: 'Pricing',
      blocks: [{
        type: 'p',
        text: 'Prices are charged per person/case, not per individual form, according to the price card in effect on the site at the time of purchase. The passport photo add-on and certified translation are separate services with their own price. **The fee the U.S. government charges for the passport is paid separately, directly to the government — Noor does not charge it or include it in its price.**'
      }]
    }, {
      heading: 'Your responsibility as a customer',
      blocks: [{
        type: 'p',
        text: 'You are responsible for making sure the data you give us (name, date of birth, place of birth, and so on) is accurate and matches your official ID documents. Before final delivery, you review and approve the prepared form.'
      }]
    }, {
      heading: 'Free-correction guarantee',
      blocks: [{
        type: 'p',
        text: 'If the form we prepared has an error on our part, we will correct it and redeliver it at no extra cost, within a reasonable time after delivery. **This guarantee covers only the form we prepared.** It does not guarantee that the government will approve, expedite, or not reject your application, and it does not cover the fee you paid to the government.'
      }]
    }, {
      heading: 'Passport photo (if purchased)',
      blocks: [{
        type: 'p',
        text: 'The photo is delivered **validated** against the official rules in effect — we never offer it as "guaranteed." If validation fails, you are entitled to a free retake; see the Refund Policy for details.'
      }]
    }, {
      heading: 'Communication and virtual assistant',
      blocks: [{
        type: 'p',
        text: "We may communicate with you via WhatsApp, phone, email, or the site's virtual assistant, in the language you chose. The virtual assistant is automated; for cases that require it, Manuel (the business's human specialist) does the final review of the form."
      }]
    }, {
      heading: 'Limits of liability',
      blocks: [{
        type: 'p',
        text: 'Noor does not control the timing or decisions of the Department of State or any government agency. We are not responsible for delays, rejections, or requirement changes outside our control, as long as the form we prepared correctly reflects the information you gave us and approved.'
      }]
    }, {
      heading: 'Changes to these terms',
      blocks: [{
        type: 'p',
        text: 'We may update these terms from time to time; the last-updated date above indicates the version in effect.'
      }]
    }, {
      heading: 'Governing law',
      blocks: [{
        type: 'p',
        text: 'These terms are governed by the laws of the State of Florida, USA.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  },
  ht: {
    title: 'Kondisyon Sèvis',
    updated: 'Dènye mizajou: 22 septanm 2026',
    intro: 'Lè ou itilize sit la oswa ou pran sèvis Noor Passport Services (DBA of Noor Metabolico LLC), ou aksepte kondisyon sa yo.',
    sections: [{
      heading: 'Sa Noor ye ak sa li PA ye',
      blocks: [{
        type: 'p',
        text: 'Noor se yon sèvis prive **preparasyon fòm** paspò. Nou ede ou ranpli kòrèkteman pwòp fòm ofisyèl ou, nan lang ou. **Noor se pa yon ajans gouvènman, se pa yon kabinè avoka, epi se pa yon notè.** Nou pa bay konsèy legal ni konsèy imigrasyon. Se ou menm ki soumèt aplikasyon final la bay Depatman Deta Etazini — se pa Noor.'
      }]
    }, {
      heading: 'Konfòmite ak Lwa Florid § 501.1391',
      blocks: [{
        type: 'p',
        text: 'Dapre Lwa Florid § 501.1391 (an vigè depi 1ye jiyè 2025), ki egzije tout moun oswa biznis ki ofri sèvis ki gen rapò ak imigrasyon epi ki pa yon avoka ki gen lisans pou pibliye yon avi vizib sou sit entènèt li ak nan kote biznis li: **"MWEN PA YON AVOKA KI GEN LISANS POU PRATIKE LWA EPI MWEN PA KA BAY KONSÈY LEGAL OSWA AKSEPTE ONORÈ POU KONSÈY LEGAL. MWEN PA AKREDITE POU REPREZANTE OU NAN ZAFÈ IMIGRASYON."**'
      }, {
        type: 'p',
        text: 'Pwosesis sèvis nou — siyen Kontra Sèvis la, peman, kesyonè, revizyon, ak livrezon — se pwòp politik entèn nou de bon pratik, ki fèt pou garanti transparans epi dokimante ke tout travay fèt dapre enstriksyon espesifik ou. **Li pa yon sitasyon egzat nan § 501.1391.** Nou pa fè ou peye anvan ou siyen kontra sèvis la.'
      }]
    }, {
      heading: 'Pri',
      blocks: [{
        type: 'p',
        text: 'Pri yo chaje pa moun/ka, se pa pa fòm endividyèl, dapre kat pri ki valab sou sit la nan moman achte a. Add-on foto paspò a ak tradiksyon sètifye a se sèvis apa, ak pwòp pri pa yo. **Frè gouvènman ameriken an chaje pou paspò a peye apa, dirèkteman bay gouvènman an — Noor pa chaje l ni li pa enkli l nan pri li.**'
      }]
    }, {
      heading: 'Responsablite ou kòm kliyan',
      blocks: [{
        type: 'p',
        text: 'Se responsablite ou pou asire done ou ban nou yo (non, dat nesans, kote nesans, elatriye) egzat epi yo koresponn ak dokiman idantite ofisyèl ou. Anvan livrezon final la, ou revize epi apwouve fòm ki prepare a.'
      }]
    }, {
      heading: 'Garanti korije gratis',
      blocks: [{
        type: 'p',
        text: 'Si fòm nou prepare a gen yon erè ki soti nan bò nou, nou korije l epi nou remèt li san okenn kou adisyonèl, nan yon delè rezonab apre livrezon. **Garanti sa a kouvri sèlman fòm nou prepare a.** Li pa garanti gouvènman an ap apwouve, akselere, oswa pa rejte aplikasyon ou, epi li pa kouvri frè ou te peye bay gouvènman an.'
      }]
    }, {
      heading: 'Foto paspò (si ou achte l)',
      blocks: [{
        type: 'p',
        text: 'Foto a livre **valide** kont règ ofisyèl ki aktyèl yo — nou pa janm ofri l kòm "garanti". Si validasyon an echwe, ou gen dwa a yon re-pran san kou; gade Politik Ranbousman an pou detay yo.'
      }]
    }, {
      heading: 'Kominikasyon ak asistan vityèl la',
      blocks: [{
        type: 'p',
        text: 'Nou ka kominike avè ou sou WhatsApp, telefòn, imèl, oswa asistan vityèl sit la, nan lang ou te chwazi a. Asistan vityèl la otomatik; pou ka ki mande sa, Manuel (espesyalis imen biznis la) fè revizyon final fòm nan.'
      }]
    }, {
      heading: 'Limit responsablite',
      blocks: [{
        type: 'p',
        text: 'Noor pa kontwole tan ni desizyon Depatman Deta a oswa nenpòt ajans gouvènman. Nou pa responsab pou reta, rejè, oswa chanjman kondisyon ki andeyò kontwòl nou, depi fòm nou prepare a reflete kòrèkteman enfòmasyon ou te ban nou epi apwouve.'
      }]
    }, {
      heading: 'Chanjman nan kondisyon sa yo',
      blocks: [{
        type: 'p',
        text: 'Nou ka mete ajou kondisyon sa yo detanzantan; dat dènye mizajou anwo a endike vèsyon ki aktyèl la.'
      }]
    }, {
      heading: 'Lwa aplikab',
      blocks: [{
        type: 'p',
        text: 'Kondisyon sa yo gouvène pa lwa Eta Florid, Etazini.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  },
  vi: {
    title: 'Điều Khoản Dịch Vụ',
    updated: 'Cập nhật lần cuối: ngày 22 tháng 9 năm 2026',
    intro: 'Khi sử dụng trang web hoặc thuê dịch vụ của Noor Passport Services (DBA of Noor Metabolico LLC), quý vị đồng ý với các điều khoản này.',
    sections: [{
      heading: 'Noor là gì và KHÔNG phải là gì',
      blocks: [{
        type: 'p',
        text: 'Noor là dịch vụ tư nhân **chuẩn bị đơn** xin hộ chiếu. Chúng tôi giúp quý vị hoàn thành chính xác đơn chính thức của mình, bằng ngôn ngữ của quý vị. **Noor không phải là cơ quan chính phủ, công ty luật, hay công chứng viên.** Chúng tôi không tư vấn pháp lý hay di trú. Chính quý vị nộp đơn cuối cùng cho Bộ Ngoại giao Hoa Kỳ — không phải Noor.'
      }]
    }, {
      heading: 'Tuân Thủ Đạo Luật Florida § 501.1391',
      blocks: [{
        type: 'p',
        text: 'Theo Đạo Luật Florida § 501.1391 (có hiệu lực từ ngày 1 tháng 7 năm 2025), yêu cầu bất kỳ cá nhân hoặc doanh nghiệp nào cung cấp dịch vụ liên quan đến di trú mà không phải là luật sư có giấy phép phải đăng thông báo rõ ràng trên trang web và tại địa điểm kinh doanh của mình: **"TÔI KHÔNG PHẢI LÀ LUẬT SƯ CÓ GIẤY PHÉP HÀNH NGHỀ LUẬT VÀ KHÔNG THỂ ĐƯỦA RA TƯ VẤN PHÁP LÝ HOẶC NHẬN PHÍ TƯ VẤN PHÁP LÝ. TÔI KHÔNG ĐƯỢC CÔNG NHẬN ĐỂ ĐẠI DIỆN CHO QUÝ VỊ TRONG CÁC VẤN ĐỀ DI TRÚ."**'
      }, {
        type: 'p',
        text: 'Quy trình dịch vụ của chúng tôi — ký Hợp Đồng Dịch Vụ, thanh toán, bảng câu hỏi, xem xét và giao hồ sơ — là chính sách nội bộ của riêng chúng tôi, được thiết kế để đảm bảo tính minh bạch và ghi nhận rằng mọi công việc được thực hiện theo chỉ dẫn cụ thể của quý vị. **Đây không phải là trích dẫn nguyên văn từ § 501.1391.** Quý vị không bị tính phí trước khi ký hợp đồng dịch vụ.'
      }]
    }, {
      heading: 'Giá cả',
      blocks: [{
        type: 'p',
        text: 'Giá được tính theo mỗi người/trường hợp, không theo từng đơn riêng lẻ, theo bảng giá hiện hành trên trang web tại thời điểm mua. Dịch vụ bổ sung ảnh hộ chiếu và dịch thuật có chứng nhận là các dịch vụ riêng biệt, có giá riêng. **Lệ phí chính phủ Hoa Kỳ thu cho hộ chiếu được thanh toán riêng, trực tiếp cho chính phủ — Noor không thu khoản này hay tính vào giá của mình.**'
      }]
    }, {
      heading: 'Trách nhiệm của quý vị với tư cách khách hàng',
      blocks: [{
        type: 'p',
        text: 'Quý vị chịu trách nhiệm đảm bảo dữ liệu quý vị cung cấp cho chúng tôi (họ tên, ngày sinh, nơi sinh, v.v.) chính xác và khớp với giấy tờ tùy thân chính thức của quý vị. Trước khi giao cuối cùng, quý vị xem xét và phê duyệt đơn đã chuẩn bị.'
      }]
    }, {
      heading: 'Bảo đảm sửa lỗi miễn phí',
      blocks: [{
        type: 'p',
        text: 'Nếu đơn chúng tôi chuẩn bị có lỗi do chúng tôi gây ra, chúng tôi sẽ sửa và giao lại miễn phí, trong thời gian hợp lý sau khi giao hàng. **Bảo đảm này chỉ áp dụng cho đơn mà chúng tôi chuẩn bị.** Nó không đảm bảo chính phủ sẽ phê duyệt, đẩy nhanh, hay không từ chối đơn của quý vị, và không bao gồm lệ phí quý vị đã trả cho chính phủ.'
      }]
    }, {
      heading: 'Ảnh hộ chiếu (nếu mua)',
      blocks: [{
        type: 'p',
        text: 'Ảnh được giao ở trạng thái **đã xác thực** theo các quy định chính thức hiện hành — chúng tôi không bao giờ khẳng định "được đảm bảo". Nếu xác thực thất bại, quý vị có quyền chụp lại miễn phí; xem Chính Sách Hoàn Tiền để biết chi tiết.'
      }]
    }, {
      heading: 'Liên lạc và trợ lý ảo',
      blocks: [{
        type: 'p',
        text: 'Chúng tôi có thể liên lạc với quý vị qua WhatsApp, điện thoại, email, hoặc trợ lý ảo của trang web, bằng ngôn ngữ quý vị đã chọn. Trợ lý ảo hoạt động tự động; đối với các trường hợp cần thiết, Manuel (chuyên viên con người của doanh nghiệp) sẽ xem xét cuối cùng đơn.'
      }]
    }, {
      heading: 'Giới hạn trách nhiệm',
      blocks: [{
        type: 'p',
        text: 'Noor không kiểm soát thời gian hay quyết định của Bộ Ngoại giao hay bất kỳ cơ quan chính phủ nào. Chúng tôi không chịu trách nhiệm về sự chậm trễ, từ chối, hay thay đổi yêu cầu nằm ngoài tầm kiểm soát của chúng tôi, miễn là đơn chúng tôi chuẩn bị phản ánh chính xác thông tin quý vị đã cung cấp và phê duyệt.'
      }]
    }, {
      heading: 'Thay đổi các điều khoản này',
      blocks: [{
        type: 'p',
        text: 'Chúng tôi có thể cập nhật các điều khoản này theo thời gian; ngày cập nhật lần cuối ở trên cho biết phiên bản hiện hành.'
      }]
    }, {
      heading: 'Luật áp dụng',
      blocks: [{
        type: 'p',
        text: 'Các điều khoản này chịu sự điều chỉnh của luật pháp Tiểu bang Florida, Hoa Kỳ.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  }
};
export function TermsOfServicePage() {
  const {
    language
  } = useLanguage();
  return <LegalPage doc={DOCS[language]} data-id="element-723" data-mp="2jjzpa77vlkkt" />;
}